import React from "react";
import { Card, Stack } from "react-bootstrap";
import { School, SCHOOL_COLORS } from "../../util/cardEffects/effectTypes.ts";
import { useCardScale } from "../../util/cardUtils.ts";

let zIndex = 1;

interface PlayingCardProps {
    name?: string;
    cost?: number;
    castThreshold?: number;
    focus?: number;
    tempFocus?: number;
    cast?: string | null;
    play?: string | null;
    sustain?: string | null;
    passive?: string | null;
    backgroundImage?: string | null;
    school?: School | null;
    scale?: number;
    verticalIndex?: number;
    faceDown?: boolean;
    highlightOnFullFocus?: boolean;
    forceNoHightlight?: boolean;
    energizeAmount?: number;
    classCard?: boolean;
    ritual?: boolean;
    inline?: boolean;
    scaleOverride?: undefined | number;
}

const ABILITY_ICONS: Record<string, string> = {
    play: "∝",
    cast: "-",
    sustain: "⧖",
    passive: "○",
    other: "+",
};

const ABILITY_LABELS: Record<string, string> = {
    play: "Play",
    cast: "Cast",
    sustain: "Sustain",
    passive: "Passive",
};

// Base card dimensions at scale = 1
export const BASE_WIDTH = 250;
export const BASE_HEIGHT = BASE_WIDTH * (7 / 5); // 5:7 ratio
export const BASE_WINDOW_WIDTH = 1536;
export const BASE_WINDOW_HEIGHT = 688.8

export const PlayingCard: React.FC<PlayingCardProps> = ({
    name = "",
    cost = 0,
    castThreshold = 0,
    focus = 0,
    tempFocus = 0,
    energizeAmount = 0,
    classCard = false,
    ritual = false,
    cast,
    play,
    sustain,
    passive,
    backgroundImage,
    school = "Manifest",
    scale = 1,
    verticalIndex = zIndex += 10,
    faceDown = false,
    highlightOnFullFocus = false,
    forceNoHightlight = false,
    inline = true,
    scaleOverride = undefined,
}) => {
    const baseScale = useCardScale();
    const modifiedScale = scaleOverride ? scaleOverride : baseScale * scale;
    const schoolColor = SCHOOL_COLORS[school ?? "Manifest"];

    const abilities = [
        { key: "play", text: play },
        { key: "cast", text: cast },
        { key: "sustain", text: sustain },
        { key: "passive", text: passive },
    ].filter((a): a is { key: string; text: string } => Boolean(a.text));

    const displayedAbilities = abilities.slice(0, 2);
    const hasMore = abilities.length > 2;

    if (faceDown) {
        return (
            <div style={{
                width: `${Math.ceil(BASE_WIDTH * modifiedScale)}px`,
                height: `${BASE_HEIGHT * modifiedScale}px`,
                overflow: "hidden",
                boxShadow: (!highlightOnFullFocus || focus + tempFocus >= cost) && !forceNoHightlight ? `0 ${4 * modifiedScale}px ${10 * modifiedScale}px ${schoolColor}40` : '',
                display: inline ? "inline-block" : "",
                borderRadius: `${8 * modifiedScale}px`,
                zIndex: verticalIndex,
            }}><Card
                    className="card-back"
                    style={{
                        transformOrigin: "top left",
                        transform: `scale(${modifiedScale})`,
                        width: `${BASE_WIDTH}px`,
                        height: `${BASE_HEIGHT}px`,
                        position: "relative",
                        backgroundColor: "#1f2937",
                        border: (!highlightOnFullFocus || focus + tempFocus >= cost) && !forceNoHightlight ? `2px solid ${schoolColor}` : '',
                        borderRadius: `8px`,
                        overflow: "hidden",
                        zIndex: verticalIndex,
                    }}
                />
            </div>
        );
    }

    return (
        <div style={{
            width: `${Math.ceil(BASE_WIDTH * modifiedScale)}px`,
            height: `${BASE_HEIGHT * modifiedScale}px`,
            overflow: "hidden",
            boxShadow: (!highlightOnFullFocus || focus + tempFocus >= cost) && !forceNoHightlight ? `0 ${4 * modifiedScale}px ${10 * modifiedScale}px ${schoolColor}40` : '',
            display: inline ? "inline-block" : "",
            borderRadius: `${8 * modifiedScale}px`,
            zIndex: verticalIndex,
        }}>
            <Card
                style={{
                    transformOrigin: "top left",
                    transform: `scale(${modifiedScale})`,
                    width: `${BASE_WIDTH}px`,
                    height: `${BASE_HEIGHT}px`,
                    position: "relative",
                    backgroundColor: "#1f2937",
                    border: (!highlightOnFullFocus || focus + tempFocus >= cost) && !forceNoHightlight ? `2px solid ${schoolColor}` : '',
                    borderRadius: `8px`,
                    overflow: "hidden",
                    zIndex: verticalIndex,
                }}
            >
                {/* Background Image */}
                {backgroundImage && (
                    <div
                        style={{
                            position: "absolute",
                            inset: 0,
                            backgroundImage: `url(${backgroundImage})`,
                            backgroundSize: "cover",
                            backgroundPosition: "center",
                            opacity: 0.3,
                            zIndex: verticalIndex + 1,
                        }}
                    />
                )}

                {/* Stars Column */}

                <Bookmark cost={cost} focus={focus} tempFocus={tempFocus} schoolColor={schoolColor} castThreshold={castThreshold} energizeAmount={energizeAmount} verticalIndex={verticalIndex} />

                {/* Content */}
                <Card.Body
                    style={{
                        position: "relative",
                        padding: `10px`,
                        paddingTop: `7px`,
                        display: "flex",
                        flexDirection: "column",
                        height: "100%",
                    }}
                >
                    {/* Header: Name + School Dot */}
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: `10px`,
                            zIndex: verticalIndex + 2,
                        }}
                    >
                        <div
                            style={{
                                width: `10px`,
                                height: `10px`,
                                borderRadius: "50%",
                                backgroundColor: schoolColor,
                                boxShadow: `0 0 6px ${schoolColor}`,
                                flexShrink: 0,
                                marginBottom: `2px`,
                                marginLeft: `2px`
                            }}
                        />
                        <Card.Title
                            style={{
                                color: schoolColor,
                                fontSize: `16px`,
                                fontWeight: "bold",
                                margin: 0,
                                textShadow: `0 0 5px ${schoolColor}60`,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                paddingBottom: `5px`,
                            }}
                            title={name}
                            className="fade-border-bottom"
                        >
                            {name?.slice(0, 23)}
                        </Card.Title>
                    </div>

                    {/* Abilities */}
                    <Stack gap={2} style={{ flex: 1, justifyContent: "flex-end" }}>
                        {hasMore && (
                            <div
                                style={{
                                    textAlign: "center",
                                    color: "#9ca3af",
                                    fontSize: `${5}px`,
                                    fontStyle: "italic",
                                }}
                            >
                                +{abilities.length - 2} more abilities
                            </div>
                        )}
                        {displayedAbilities
                            .map(({ key, text }) => {
                                return <AbilityDisplay abilityType={key} schoolColor={schoolColor} text={text} />
                            })
                            .slice(0, 2)}
                    </Stack>

                    {/* School Label */}
                    <div
                        className="fade-border-top"
                        style={{
                            textAlign: "center",
                            color: schoolColor,
                            fontSize: `12px`,
                            fontWeight: "bold",
                            textTransform: "uppercase",
                            letterSpacing: `2}px`,
                            paddingTop: `7px`,
                            marginTop: `4px`,
                        }}
                    >
                        <div
                            style={{ isolation: "isolate", opacity: 1, color: schoolColor }}
                        >
                            {school}{classCard ? " / Class" : ""}{ritual ? " / ritual" : ""}
                        </div>
                    </div>
                </Card.Body>
            </Card>
        </div>
    );
};

export function AbilityDisplay({ abilityType: key, removeBottomMargin = false, schoolColor = SCHOOL_COLORS["Manifest"], text }:
    {
        abilityType: string,
        schoolColor?: string,
        text: string
        removeBottomMargin?: boolean
    }) {

    return <div
        key={key}
        style={{
            backgroundColor: "#374151",
            borderRadius: removeBottomMargin ? `0 0 8px 8px` : `8px`,
            padding: `3px 10px 5px 10px`,
            borderLeft: `3px solid ${schoolColor}`,
            marginBottom: removeBottomMargin ? `0px` : `5px`,
        }}
    >
        <span
            style={{
                color: schoolColor ?? SCHOOL_COLORS["Manifest"],
                fontWeight: "bold",
                marginRight: `4px`,
                fontSize: `15px`,
            }}
        >
            {ABILITY_ICONS[key]} {ABILITY_LABELS[key]}:
        </span>
        <span
            style={{ color: "#e5e7eb", fontSize: `15px` }}
        >
            {text}
        </span>
    </div>
}

export function Bookmark({ verticalIndex = 1, schoolColor = SCHOOL_COLORS["Manifest"], cost, tempFocus = 0, castThreshold = 0, focus = 0, energizeAmount = 0 }:
    {
        verticalIndex?: number,
        schoolColor?: string,
        cost: number,
        tempFocus?: number,
        castThreshold?: number,
        focus?: number,
        energizeAmount?: number
    }) {

    const stars = [];
    const starSize = 8;

    const focusStar = (key: number, shape: number = 0, white: boolean = false, transparent: boolean = false, overline = false) => {
        return <div
            key={key}
            className={shape === 0 ? "circle" : (shape === 1 ? "four-star" : "heptogram")}
            style={{
                width: 0,
                height: 0,
                borderLeft: `${starSize}px solid transparent`,
                borderRight: `${starSize}px solid transparent`,
                borderBottom: `${starSize * 2}px solid ${white ? "#fff" : schoolColor}`,
                marginBottom: 3,
                position: "relative",
                backgroundColor: `${white ? "#fff" : schoolColor}`,
                opacity: transparent ? 0.5 : 1,
                marginTop: 4,
            }}
        />
    }

    let symbols = Math.min(10, Math.max(0, cost));
    if (focus > cost && symbols > 9) {
        symbols--;
    }
    if (focus + tempFocus > cost && tempFocus > 0 && symbols > 8) {
        symbols--;
    }

    for (let i = 0; i < Math.max(symbols, castThreshold); i++) {

        let shape = 0;
        if (i < focus) shape++;
        if (i < focus + tempFocus) shape++;

        stars.push(<div style={{ borderBottom: (i + 1 === energizeAmount ? "1px solid white" : "") }}>{focusStar(i, shape, i >= focus + tempFocus, i >= focus + tempFocus || i < castThreshold)}</div>)
    }

    if (cost === -1) {
        stars.push(
            <div
                key="x cost"
                style={{
                    fontSize: `10px`,
                    color: schoolColor,
                }}
            >
                X
            </div>
        );
    }

    if (focus > symbols) {
        stars.push(
            <div
                key="focus"
                style={{
                    fontSize: `10px`,
                    color: schoolColor,
                }}
            >
                +{focus - symbols}
            </div>
        );
    }

    if ((focus > cost && tempFocus > 0) || (focus + tempFocus > cost && tempFocus > 0)) {
        stars.push(
            <div
                key="temp focus"
                style={{
                    fontSize: `10px`,
                    color: schoolColor,
                }}
            >
                +{tempFocus - Math.max(0, (symbols - focus))}
            </div>
        );
    }

    return <div
        style={{
            position: "absolute",
            top: 0,
            right: `8px`,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            paddingBottom: `3px`,
            paddingTop: `3px`,
            paddingInline: `3px`,
            backgroundColor: "rgba(0,0,0,.3)",
            borderBottomLeftRadius: `10px`,
            borderBottomRightRadius: `10px`,
            zIndex: verticalIndex + 2,
        }}
    >{stars}</div>;
}